import { Component, signal, inject, computed } from '@angular/core';
import { Router, RouterLink, NavigationEnd } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs/operators';
import { AuthService } from '../../core/services/auth.service';

interface NavLink { name: string; href: string; prefix: string; }

const NAV_LINKS: NavLink[] = [
  { name: 'Plan',    href: '/plan',          prefix: '/plan' },
  { name: 'Posts',   href: '/posts',         prefix: '/post' },
  { name: 'Content', href: '/content/queue', prefix: '/content' },
  { name: 'Studio',  href: '/music-library', prefix: '/music' },
];

@Component({
  selector: 'sw-topbar',
  standalone: true,
  imports: [RouterLink],
  template: `
    <nav class="navbar" [class.scrolled]="scrolled()">
      <div class="inner glass" [class.scrolled]="scrolled()">

        <!-- Logo -->
        <a routerLink="/plan" class="logo">
          <div class="logo-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
              <polyline points="4 17 10 11 4 5"/><line x1="12" y1="19" x2="20" y2="19"/>
            </svg>
          </div>
          <span class="logo-text">Stonewave <span class="grad">Prism</span></span>
        </a>

        <!-- Main nav -->
        <div class="nav-links">
          @for (link of links; track link.href) {
            <a [routerLink]="link.href" class="nav-link" [class.active]="isActive(link.prefix)">
              {{ link.name }}
            </a>
          }
        </div>

        <div class="spacer"></div>

        <!-- Right -->
        <div class="right">
          <button class="exit-btn" (click)="auth.signOut()">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            <span>Exit</span>
          </button>
        </div>
      </div>
    </nav>
  `,
  styles: [`
    :host { display: block; }

    .navbar {
      position: fixed;
      top: 0; left: 0; right: 0;
      z-index: 100;
      padding: 32px 24px;
      transition: padding 0.5s cubic-bezier(0.16,1,0.3,1);
    }
    .navbar.scrolled { padding: 16px 24px; }

    .inner {
      border-radius: 9999px;
      padding: 10px 10px 10px 20px;
      display: flex;
      align-items: center;
      gap: 8px;
      transition: background 0.5s, border-color 0.5s, box-shadow 0.5s;
    }
    .inner.scrolled {
      background: rgba(11,14,20,0.65);
      backdrop-filter: blur(24px);
      border-color: rgba(255,255,255,0.1);
      box-shadow: 0 0 40px rgba(0,0,0,0.5);
    }

    .logo {
      display: flex; align-items: center; gap: 10px;
      text-decoration: none; color: var(--white);
      margin-right: 8px;
    }
    .logo-icon {
      width: 32px; height: 32px; border-radius: 8px;
      background: var(--cyan); color: var(--obsidian);
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 0 15px rgba(0,210,255,0.3);
      transition: transform 0.2s var(--ease-spring);
      flex-shrink: 0;
    }
    .logo:hover .logo-icon { transform: scale(1.1); }
    .logo-text {
      font-family: var(--font-grotesk);
      font-size: 19px;
      font-weight: 900;
      font-style: italic;
      text-transform: uppercase;
      letter-spacing: -0.03em;
      white-space: nowrap;
    }
    .grad {
      background: linear-gradient(to right, #fff, var(--cyan));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    /* Nav links */
    .nav-links {
      display: none;
      align-items: center;
      gap: 2px;
    }
    @media (min-width: 768px) { .nav-links { display: flex; } }

    .nav-link {
      font-family: var(--font-mono);
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 0.2em;
      color: rgba(255,255,255,0.4);
      text-decoration: none;
      padding: 8px 14px;
      border-radius: 9999px;
      transition: color 0.15s, background 0.15s;
    }
    .nav-link:hover { color: rgba(255,255,255,0.8); }
    .nav-link.active {
      color: var(--white);
      background: rgba(255,255,255,0.07);
    }

    .spacer { flex: 1; }

    .right { display: flex; align-items: center; gap: 4px; }

    .exit-btn {
      display: flex; align-items: center; gap: 6px;
      background: none; border: none; cursor: pointer;
      font-family: var(--font-mono);
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 0.2em;
      color: rgba(239,68,68,0.6);
      padding: 8px 14px;
      border-radius: 9999px;
      transition: color 0.15s, background 0.15s;
    }
    .exit-btn:hover { color: rgb(239,68,68); background: rgba(239,68,68,0.08); }
  `],
})
export class TopbarComponent {
  readonly auth   = inject(AuthService);
  readonly router = inject(Router);
  scrolled = signal(false);
  links    = NAV_LINKS;

  private readonly url = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(e => (e as NavigationEnd).urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  isActive(prefix: string): boolean {
    const url = this.url() ?? '';
    if (prefix === '/post') return url.startsWith('/post') || url.startsWith('/posts');
    if (prefix === '/music') return url.startsWith('/music') || url.startsWith('/brand') || url.startsWith('/export');
    return url.startsWith(prefix);
  }

  constructor() {
    const onScroll = () => this.scrolled.set(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
  }
}
